import { AspectRatio, Box, HStack, Image } from "@chakra-ui/react";
import { useConfigurationMenu } from "@raiden/library/hooks/useConfigurationMenu";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { memo, useMemo, useState } from "react";
import { CenteredContent } from "../../../../../components/CenteredContent";
import { Href } from "../../../../../components/Href";
import { CONTENT_WIDTH } from "../../../../../constants/Layout";
import { Z_INDEX } from "../../../../../constants/zIndex";
import { MainMenuItem } from "./MainMenuItem";

export const HEADER_HEIGHT = 90;

export const HeaderDesktop = memo(
  /**
   * @typedef {object} Props
   * @property {boolean} isScrolled
   * @property {boolean} isHomePage
   * @property {string} backgroundColor
   */

  /**
   * @param {Props} props
   */
  function HeaderDesktop({ isScrolled, isHomePage, backgroundColor }) {
    const { menu } = useConfigurationMenu({ alias: "header" });

    const [activeMenuId, setActiveMenuId] = useState(
      /** @type {number | null} */ (null),
    );

    const router = useRouter();

    const translate = useTranslate();

    const logoFilePath = useMemo(() => {
      if (!isHomePage) {
        return "logo.svg";
      }

      return isScrolled ? "logo.svg" : "logo-white.svg";
    }, [isHomePage, isScrolled]);

    /**
     * @param {{menuItem: import("@raiden/library/types/Configuration").ConfigurationMenuItem}} menuItem
     * @returns {boolean}
     * */
    const isMenuRouterActive = ({ menuItem }) => {
      if (router.asPath === "/" && translate(menuItem.url) === "/") {
        return true;
      } else {
        if (
          translate(menuItem.url)?.includes(router.asPath) &&
          router.asPath !== "/"
        ) {
          return true;
        }

        if (menuItem.object_type === "post") {
          // Particular case for savoir-page page post
          if (router.asPath.includes(`${translate(menuItem.object?.slug)}`)) {
            return true;
          }
        }
      }

      return false;
    };

    return (
      <Box
        display={{ base: "none", lg: "block" }}
        position="fixed"
        top="0"
        left="0"
        right="0"
        px=".625rem"
        zIndex={Z_INDEX.HEADER_DROP_DOWN}
        transition="all 0.3s"
        borderBottom={isScrolled ? "1px solid" : "none"}
        borderBottomColor="brandSecondary.100"
        backgroundColor={isScrolled ? "white" : backgroundColor}
        boxShadow={isScrolled ? "sm" : "none"}>
        <CenteredContent>
          <Box maxW={CONTENT_WIDTH}>
            <HStack
              as="nav"
              alignItems="flex-start"
              justifyContent="space-between"
              height={`${HEADER_HEIGHT}px`}>
              <Box flexShrink={0} py="1rem" w="176px">
                <Href
                  href={generateFrontPath({
                    id: "@front.home",
                  })}>
                  <AspectRatio ratio={176 / 60}>
                    <Image
                      src={generateFrontPath({
                        id: "@front.internal-assets",
                        parameters: {
                          filePath: logoFilePath,
                        },
                        includeBasePath: true,
                      })}
                      alt="Logo"
                      transition="filter 0.3s ease"
                    />
                  </AspectRatio>
                </Href>
              </Box>

              <HStack spacing="0" w="full" alignSelf="stretch">
                {menu?.items.map((menuItem, index) => (
                  <MainMenuItem
                    menuItem={menuItem}
                    key={index}
                    isActive={
                      activeMenuId === menuItem.id ||
                      isMenuRouterActive({ menuItem })
                    }
                    withWhiteBackground={isScrolled || !isHomePage}
                    setActiveMenuId={setActiveMenuId}
                  />
                ))}
              </HStack>
            </HStack>
          </Box>
        </CenteredContent>
      </Box>
    );
  },
);
